import * as React from "react";
import { Form, Input, Button, message } from "antd";
import { GlobalOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import { authApi } from "../api/auth";
import { buildAuth0AuthorizeUrl } from "../config/authConfig";
import { useLogin } from "../hooks/useAuth";
import { useAuthStore } from "../stores/useAuthStore";
import { colors } from "../theme/colors";

const LOGO_SRC = "/assets/LogoCenna_black.png";

export const LOGIN_URL_STORAGE_KEY = "cennaWordAddInLoginUrl";

interface LoginFormValues {
  loginUrl: string;
  username: string;
  password: string;
}

function isValidLoginUrl(value: string): boolean {
  const t = (value || "").trim();
  if (!t) return false;
  if (t.startsWith("/")) return true;
  try {
    const parsed = new URL(t);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Open Auth0 via the Office Dialog API.
 * The dialog navigates to Auth0, which redirects to auth-callback.html.
 * That page calls messageParent with the id_token.
 */
function openSsoDialog(
  authorizeUrl: string,
  loginUrl: string,
  setAccessToken: (t: string) => void,
  onDone: () => void
): void {
  Office.context.ui.displayDialogAsync(
    authorizeUrl,
    { height: 60, width: 40, promptBeforeOpen: false },
    (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        message.error(`Could not open sign-in window: ${asyncResult.error.message}`);
        onDone();
        return;
      }

      const dialog = asyncResult.value;

      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        async (arg: { message: string } | { error: number }) => {
          dialog.close();

          if ("error" in arg) {
            message.error("Sign-in dialog was closed unexpectedly.");
            onDone();
            return;
          }

          let parsed: { id_token?: string; error?: string };
          try {
            parsed = JSON.parse(arg.message);
          } catch {
            message.error("Received invalid response from sign-in window.");
            onDone();
            return;
          }

          if (parsed.error) {
            message.error(parsed.error);
            onDone();
            return;
          }

          if (!parsed.id_token) {
            message.error("No authentication token received.");
            onDone();
            return;
          }

          try {
            const result = await authApi.ssoCallback({
              loginUrl,
              idToken: parsed.id_token,
            });
            setAccessToken(result.accessToken);
            message.success("Login successful!");
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "SSO login failed.";
            message.error(msg);
          } finally {
            onDone();
          }
        }
      );

      dialog.addEventHandler(
        Office.EventType.DialogEventReceived,
        () => {
          onDone();
        }
      );
    }
  );
}

const Login: React.FC = () => {
  const themeColors = colors.colors;
  const [form] = Form.useForm<LoginFormValues>();
  const loginUrlValue: string = Form.useWatch("loginUrl", form) ?? "";
  const urlValid = isValidLoginUrl(loginUrlValue);
  const [ssoLoading, setSsoLoading] = React.useState(false);
  const { mutate: login, isPending } = useLogin();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  const loginUrlInitial = React.useMemo(() => {
    try {
      return localStorage.getItem(LOGIN_URL_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  }, []);

  const handleSubmit = (values: LoginFormValues) => {
    const trimmedLoginUrl = values.loginUrl.trim();
    if (!isValidLoginUrl(trimmedLoginUrl)) {
      message.error("Please enter a valid Login URL first.");
      return;
    }

    login(
      {
        loginUrl: trimmedLoginUrl,
        username: values.username.trim(),
        password: values.password,
      },
      {
        onSuccess: () => {
          try {
            const base = trimmedLoginUrl.replace(/\/+$/, "");
            if (base) {
              localStorage.setItem(LOGIN_URL_STORAGE_KEY, base);
            }
          } catch {
            // ignore
          }
          message.success("Login successful!");
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : "Invalid username or password";
          message.error(msg);
        },
      }
    );
  };

  const handleSsoLogin = async () => {
    const trimmed = (loginUrlValue || "").trim();
    if (!isValidLoginUrl(trimmed)) {
      message.error("Please enter a valid Login URL first.");
      return;
    }

    setSsoLoading(true);
    try {
      const base = trimmed.replace(/\/+$/, "");
      if (base) {
        try { localStorage.setItem(LOGIN_URL_STORAGE_KEY, base); } catch { /* ignore */ }
      }

      const config = await authApi.ssoConfig(trimmed);
      if (!config.ssoEnabled) {
        message.error("SSO is not enabled for this organization.");
        setSsoLoading(false);
        return;
      }

      const auth0Url = buildAuth0AuthorizeUrl(
        config.auth0Domain,
        config.auth0ClientId,
        config.auth0Connection,
      );
      const dialogUrl = `${window.location.origin}/auth-callback.html?auth0_url=${encodeURIComponent(auth0Url)}`;

      openSsoDialog(
        dialogUrl,
        trimmed,
        setAccessToken,
        () => setSsoLoading(false)
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to start SSO login.";
      message.error(msg);
      setSsoLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        boxSizing: "border-box",
        overflow: "auto",
        backgroundColor: themeColors.base.white,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          padding: "24px 16px 28px",
          borderRadius: 16,
          background: themeColors.base.white,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <img
            src={LOGO_SRC}
            alt=""
            style={{ height: 26, width: "auto", objectFit: "contain" }}
          />
        </div>
        <div style={{ maxWidth: 620, margin: "0 auto", textAlign: "center" }}>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 600,
              margin: "0 0 8px",
              color: themeColors.neutral.deepGrey,
              letterSpacing: -0.2,
              lineHeight: 1.2,
            }}
          >
            Welcome back
          </h1>
          <p style={{ margin: "0 0 8px", color: themeColors.neutral.grey, fontSize: 16 }}>
            Sign in to access your account
          </p>
        </div>

        <div style={{ maxWidth: 620, margin: "0 auto", textAlign: "left" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark
            style={{ textAlign: "left" }}
            initialValues={{ loginUrl: loginUrlInitial }}
          >
            <Form.Item
              name="loginUrl"
              label="Login URL"
              rules={[
                { required: true, message: "Please enter the login endpoint URL" },
                {
                  validator: async (_, value: string) => {
                    const t = (value || "").trim();
                    if (!t) return;
                    if (t.startsWith("/")) return;
                    try {
                      const parsed = new URL(t);
                      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
                        throw new Error();
                      }
                    } catch {
                      throw new Error("Enter a valid http(s) URL or a path starting with /");
                    }
                  },
                },
              ]}
            >
              <Input
                prefix={<GlobalOutlined style={{ color: themeColors.neutral.grey }} />}
                placeholder="Login URL"
                size="large"
                style={{ borderRadius: 4, height: 48, fontSize: 14 }}
              />
            </Form.Item>

            <Form.Item
              name="username"
              rules={[{ required: true, message: "Please enter your username" }]}
            >
              <Input
                prefix={<MailOutlined style={{ color: themeColors.neutral.grey }} />}
                placeholder="Username"
                size="large"
                autoComplete="username"
                style={{ borderRadius: 4, height: 48, fontSize: 16 }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: "Please enter your password" }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: themeColors.neutral.grey }} />}
                placeholder="Password"
                size="large"
                style={{ borderRadius: 4, height: 48, fontSize: 16 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isPending}
                disabled={isPending || ssoLoading}
                style={{
                  backgroundColor: themeColors.base.primary,
                  color: themeColors.base.white,
                  borderRadius: 4,
                  outline: "none",
                  border: "none",
                  boxShadow: "none",
                  height: 48,
                  fontSize: 16,
                }}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "22px 0" }}>
            <div style={{ flex: 1, borderTop: `1px solid ${themeColors.neutral.lightGrey}` }} />
            <span style={{ color: themeColors.neutral.grey, fontSize: 14 }}>or</span>
            <div style={{ flex: 1, borderTop: `1px solid ${themeColors.neutral.lightGrey}` }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              size="large"
              block
              disabled={!urlValid || ssoLoading || isPending}
              loading={ssoLoading}
              onClick={handleSsoLogin}
              style={{
                backgroundColor: urlValid && !ssoLoading ? themeColors.base.primary : undefined,
                color: themeColors.base.white,
                borderRadius: 4,
                outline: "none",
                border: "none",
                boxShadow: "none",
                height: 48,
                fontSize: 16,
              }}
            >
              Sign in with SSO
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
